// Shared predicates for every product config flow. These answer "what does this
// product need from the customer?" and must stay the single source of truth.
//
// Pure product-shape rules, with no store access — the same layer as
// imageRecords beside it, which is why they live here rather than under any one
// flow's directory. All three configurators consume them:
//
//   * the single-product screen — the picker (BackgroundsPosesSelector), the
//     mobile entry point (SelectedPosesAndBackgrounds), the desktop auto-open
//     effect (RegularProductConfig), and the add-to-cart gating
//     (productConfigSlice, via selectors/productConfig);
//   * the multi-config bundle — childConfiguration and MultiConfig;
//   * the BYO/YPX slot wizard — getNextUrl's step routing (components/v3/
//     productConfig/utils) and its completion gate (selectors/configFlow).
//
// Diverging copies of these formulas previously left mobile users with no way to
// open the pose picker for pose-only products (single-product screen), and
// stranded parents on a background step the flow would not collect (ENG-2630 /
// ENG-2552, wizard and bundle).

import {
  backgroundSlotsCount,
  getImageRecords,
  hasIndividualRecord,
} from './imageRecords';

export interface ProductLike {
  poses_count?: number | null;
  // The same total under the two names the API uses: `background_options_count`
  // on a top-level product, `backgrounds_count` on a BYO/YPX child (see
  // _pricing_model_product.json.jbuilder). backgroundSlotsCount reads either,
  // and the wizard passes children here, so both belong in the shape.
  background_options_count?: number | null;
  backgrounds_count?: number | null;
  is_digital_download?: boolean;
  digital_download_background_type?: string | null;
  // Per-product opt-OUT: absent means the product allows crops, so this is
  // tested against `false` rather than for truthiness.
  crop_options_enabled?: boolean | null;
}

// The shoot fields the crop rule needs. Both wizards call the shoot `event`,
// but it is the same `state.shoot` slice in every case.
interface ShootLike {
  crop_options_enabled?: boolean | null;
  crop_options?: unknown[] | null;
}

// Digital downloads that resolve backgrounds automatically (none, or all of
// them) have no background step in the config flow.
//
// Private on purpose: this is half the question, and every caller wants the
// whole one — "does this product need a background from the customer?" — which
// is productNeedsBackgroundSelection below, this plus `backgroundSlotsCount > 0`.
// Both the BYO/YPX navigation (getNextUrl) and its completion gate
// (selectBackgroundsSatisfied) must read that same combined predicate: splitting
// the two conditions across gate and navigation is what stranded a parent on
// backgrounds the flow would not collect (ENG-2630), so exporting this half
// again would hand the next caller the shape of that bug.
const productSkipsBackgroundSelection = (
  product: ProductLike | null | undefined,
): boolean =>
  Boolean(
    product?.is_digital_download &&
      (product?.digital_download_background_type === 'no_background' ||
        product?.digital_download_background_type === 'all_backgrounds'),
  );

// Whether a background has to be collected FROM THE CUSTOMER for this product.
// The canonical answer; every flow's navigation and every completion gate reads
// this one predicate (see the callers listed at the top of this file).
//
// An all_backgrounds digital download still reports a background count >= 1,
// but the customer never picks one — background-skipped products need no
// background selection regardless of their count.
//
// WHY a false answer means "skip the step", since the reason is easy to guess
// wrong and has been: NOT because the picker would be empty. The background step
// (components/v3/productConfig/Background.tsx, and BackgroundsConfig on the
// single-product screen) renders `shoot.backgroundOptions` — the shoot's
// catalogue — and never reads the product, so a product this returns false for
// is shown the full grid and can be picked from normally.
//
// It is skipped because the product has no background to composite the choice
// onto, and nothing downstream rejects the choice either: it is stored on the
// slot, counted by the completion gates, shipped to the lab by buildCartObject,
// and can be billed as a background upgrade. That stray tap is why team-print
// order items carry background codes today, and why
// Order#missing_green_screen_images? read those codes as proof the order was
// complete (ENG-2300 / ENG-2552).
//
// So what this prevents is a wrong order, not a stuck customer — do not widen or
// relax the rule on reasoning about a picker that would have rendered fine.
export const productNeedsBackgroundSelection = (
  product: ProductLike | null | undefined,
): boolean =>
  backgroundSlotsCount(product) > 0 &&
  !productSkipsBackgroundSelection(product);

// Crop options apply to INDIVIDUAL poses only: the shoot has to enable them and
// offer at least one style, the product must not opt out, and the product must
// expose at least one individual pose slot. A pure group product — a class photo
// and nothing else — has nothing to crop, and excluding it here is what keeps
// every caller from re-deriving the rule with image_type special-casing.
//
// This is the CAPABILITY question only; callers add their own context. Both
// wizards also require prepay, and getNextUrl additionally skips a slot that has
// already collected its crops. Asking it four different ways is how the BYO/YPX
// wizard came to route pure group products to a crop page the single-product
// screen and the multi-config bundle both refuse them.
export const productOffersCropOptions = (
  product: ProductLike | null | undefined,
  shoot: ShootLike | null | undefined,
): boolean =>
  Boolean(shoot?.crop_options_enabled) &&
  product?.crop_options_enabled !== false &&
  (shoot?.crop_options?.length ?? 0) > 0 &&
  hasIndividualRecord(getImageRecords(product));
